/**
 * Event Display Utilities
 *
 * Helper functions for formatting and displaying calendar events.
 */

/**
 * Format a Date or ISO string to 12-hour time (e.g., "9:00 AM")
 */
export function formatTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format event time range (e.g., "9:00 AM - 10:30 AM")
 */
export function formatEventTimeRange(start: Date | string, end?: Date | string | null): string {
  const startTime = formatTime(start);
  if (!end) return startTime;
  const endTime = formatTime(end);
  return `${startTime} - ${endTime}`;
}

// Cache for memoized light background colors
const lightBackgroundCache = new Map<string, string>();

// Cache for memoized dark background colors
const darkBackgroundCache = new Map<string, string>();

/**
 * Create a light tinted background from accent color
 * Useful for creating subtle event backgrounds that pair with bold borders
 * Results are cached to avoid repeated calculations during scrolling
 */
export function getLightBackground(accentColor: string): string {
  const cached = lightBackgroundCache.get(accentColor);
  if (cached) return cached;

  const hex = accentColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const blend = 0.15;
  const newR = Math.round(255 - (255 - r) * blend);
  const newG = Math.round(255 - (255 - g) * blend);
  const newB = Math.round(255 - (255 - b) * blend);
  const result = `rgb(${newR}, ${newG}, ${newB})`;

  lightBackgroundCache.set(accentColor, result);
  return result;
}

// Dark mode base color (AMOLED black)
const DARK_BASE_R = 0x00;
const DARK_BASE_G = 0x00;
const DARK_BASE_B = 0x00;

/**
 * Create a dark tinted background from accent color
 * Blends the accent color toward black (#000000) instead of white
 * Useful for dark mode event backgrounds
 */
export function getDarkBackground(accentColor: string): string {
  const cached = darkBackgroundCache.get(accentColor);
  if (cached) return cached;

  const hex = accentColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const blend = 0.15; // Subtle tint of accent on dark base
  const newR = Math.round(DARK_BASE_R + (r - DARK_BASE_R) * blend);
  const newG = Math.round(DARK_BASE_G + (g - DARK_BASE_G) * blend);
  const newB = Math.round(DARK_BASE_B + (b - DARK_BASE_B) * blend);
  const result = `rgb(${newR}, ${newG}, ${newB})`;

  darkBackgroundCache.set(accentColor, result);
  return result;
}
